import { FC, PropsWithChildren, createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box } from '@/components/lib/components/Box';
import { BottomSheetModalMethods } from '@gorhom/bottom-sheet/lib/typescript/types';

import { InfoSheet } from './InfoSheet';
import { Modal } from './Modal';

type ImportSheetCopy = {
    title: string;
    description: string;
};

export type EntryImportSheet = {
    showImportingEntrySheet: (copy: ImportSheetCopy) => void;
    dismissImportingEntrySheet: () => void;
};

export const LoadingEntryImportSheetContext = createContext<EntryImportSheet | undefined>(undefined);

export const useLoadingEntryImportSheet = () => {
    const context = useContext(LoadingEntryImportSheetContext);
    if (context === undefined) {
        throw new Error('useLoadingEntryImportSheet must be used within a LoadingEntryImportSheetProvider');
    }
    return context;
};

export const LoadingEntryImportSheetProvider: FC<PropsWithChildren> = ({ children }) => {
    const { t } = useTranslation('common');
    const entryImportSheetRef = useRef<BottomSheetModalMethods | null>(null);
    const isPresented = useRef(false);
    const pendingDismiss = useRef(false);

    // there were some cases where dismiss was called while the sheet is still presenting,
    // which would leave the sheet stuck (open)
    // in those cases, we dismiss the sheet once it's fully presented
    const handleSheetChange = useCallback((index: number) => {
        if (index >= 0) {
            isPresented.current = true;
            if (pendingDismiss.current) {
                pendingDismiss.current = false;
                entryImportSheetRef.current?.dismiss();
            }
        } else {
            isPresented.current = false;
        }
    }, []);

    const [copy, setCopy] = useState<ImportSheetCopy>({ title: '', description: '' });

    const loadingEntryImportSheet = useMemo(
        () => ({
            showImportingEntrySheet: (sheetCopy: ImportSheetCopy) => {
                setCopy(sheetCopy);
                pendingDismiss.current = false;
                entryImportSheetRef.current?.present();
            },
            dismissImportingEntrySheet: () => {
                if (isPresented.current) {
                    entryImportSheetRef.current?.dismiss();
                } else {
                    pendingDismiss.current = true;
                }
            },
        }),
        []
    );

    return (
        <LoadingEntryImportSheetContext.Provider value={loadingEntryImportSheet}>
            <Box flex={1} overflow={'hidden'}>
                {children}
                <Modal id="loading-entry-import-sheet" modalRef={entryImportSheetRef} onChange={handleSheetChange}>
                    <InfoSheet
                        modalRef={entryImportSheetRef}
                        title={copy.title}
                        description={copy.description}
                        buttonLabel={t('dismiss')}
                    />
                </Modal>
            </Box>
        </LoadingEntryImportSheetContext.Provider>
    );
};
